// Verified Tesla Battery & Drive Unit Limited Warranty terms (US, 2025).
// Source: Tesla.com/support/vehicle-warranty + findmyelectric guide, cross-verified Dec 2025.
// All Tesla packs (except Roadster) include a guaranteed minimum 70% capacity retention.

export const WARRANTY_TABLE = [
  {
    model: "Model S",
    trims: "All trims (incl. Plaid)",
    years: 8,
    miles: 150000,
    threshold: 70,
  },
  {
    model: "Model 3",
    trims: "RWD",
    years: 8,
    miles: 100000,
    threshold: 70,
  },
  {
    model: "Model 3",
    trims: "Premium LR RWD / LR AWD / Performance AWD",
    years: 8,
    miles: 120000,
    threshold: 70,
  },
  {
    model: "Model X",
    trims: "All trims (incl. Plaid)",
    years: 8,
    miles: 150000,
    threshold: 70,
  },
  {
    model: "Model Y",
    trims: "RWD / AWD",
    years: 8,
    miles: 100000,
    threshold: 70,
  },
  {
    model: "Model Y",
    trims: "Premium LR RWD / LR AWD / Performance AWD",
    years: 8,
    miles: 120000,
    threshold: 70,
  },
  {
    model: "Cybertruck",
    trims: "All trims",
    years: 8,
    miles: 150000,
    threshold: 70,
  },
  {
    model: "Roadster (next-gen)",
    trims: "2026 announcement — terms TBD",
    years: 8,
    miles: 150000,
    threshold: 70, // estimated based on Tesla's standard 70% guarantee
    estimated: true,
  },
  {
    model: "Mustang Mach-E",
    trims: "All trims (SR & Extended Range)",
    years: 8,
    miles: 100000,
    threshold: 70,
  },
  {
    model: "F-150 Lightning",
    trims: "All trims (Pro / XLT / Lariat / Platinum)",
    years: 8,
    miles: 100000,
    threshold: 70,
  },
  {
    model: "E-Transit",
    trims: "All configurations",
    years: 8,
    miles: 100000,
    threshold: 70,
  },
];

// Demo vehicles used by the Vehicle Picker on the Live Demo dashboard.
// SoH / odometer / range are illustrative; warranty terms are accurate per the table above.
export const VEHICLES = [
  {
    id: "model-3-lr",
    name: "Model 3 Long Range",
    short: "Model 3 LR",
    year: 2021,
    color: "Pearl White",
    vin: "5YJ3E1EB7MF••••42",
    odometer: 88412,
    epaRange: 358,
    currentRange: 320,
    sohCurrent: 89.4,
    fleetAvg: 91.1,
    delta30d: -0.4,
    warranty: { years: 8, miles: 120000, threshold: 70 },
  },
  {
    id: "model-s-lr",
    name: "Model S Long Range",
    short: "Model S LR",
    year: 2022,
    color: "Midnight Silver",
    vin: "5YJSA1E62N1••••08",
    odometer: 54320,
    epaRange: 405,
    currentRange: 372,
    sohCurrent: 91.9,
    fleetAvg: 92.6,
    delta30d: -0.2,
    warranty: { years: 8, miles: 150000, threshold: 70 },
  },
  {
    id: "model-s-plaid-2021",
    name: "Model S Plaid",
    short: "Model S Plaid",
    year: 2021,
    color: "Pearl White Multi-Coat",
    vin: "5YJSA1E69MF••••27",
    odometer: 51820,
    epaRange: 348, // EPA-rated (per evspecifications.com / Edmunds 2021 spec sheet)
    currentRange: 318,
    sohCurrent: 91.4,
    fleetAvg: 92.0,
    delta30d: -0.3,
    warranty: { years: 8, miles: 150000, threshold: 70 },
    specs: {
      power: "1,020 hp · tri-motor",
      zeroToSixty: "1.99 s (Tesla claim)",
      topSpeed: "200 mph",
      packKwh: "~99.3 kWh",
      source: "tesla.com / EPA 2021 spec sheet",
    },
  },
  {
    id: "model-y-lr",
    name: "Model Y Long Range",
    short: "Model Y LR",
    year: 2023,
    color: "Deep Blue Metallic",
    vin: "7SAYGDEE5PF••••91",
    odometer: 41205,
    epaRange: 330,
    currentRange: 312,
    sohCurrent: 94.5,
    fleetAvg: 94.1,
    delta30d: -0.1,
    warranty: { years: 8, miles: 120000, threshold: 70 },
  },
  {
    id: "model-x-plaid",
    name: "Model X Plaid",
    short: "Model X Plaid",
    year: 2022,
    color: "Solid Black",
    vin: "7SAXCBE69NF••••03",
    odometer: 62810,
    epaRange: 333,
    currentRange: 301,
    sohCurrent: 90.4,
    fleetAvg: 90.9,
    delta30d: -0.3,
    warranty: { years: 8, miles: 150000, threshold: 70 },
  },
  {
    id: "cybertruck",
    name: "Cybertruck Dual Motor",
    short: "Cybertruck",
    year: 2024,
    color: "Stainless",
    vin: "7G2CEHED6RA••••22",
    odometer: 14082,
    epaRange: 340,
    currentRange: 332,
    sohCurrent: 97.6,
    fleetAvg: 97.2,
    delta30d: -0.1,
    warranty: { years: 8, miles: 150000, threshold: 70 },
  },
  {
    id: "roadster-2026",
    name: "Roadster (next-gen)",
    short: "Roadster 2026",
    year: 2026,
    color: "Velocity Red (announced)",
    vin: "PREVIEW · unreleased",
    odometer: 0,
    epaRange: 620,
    currentRange: 620,
    sohCurrent: 100,
    fleetAvg: 100,
    delta30d: 0,
    warranty: { years: 8, miles: 150000, threshold: 70, estimated: true },
    isPreview: true,
    announced: {
      zeroToSixty: "1.9 s (claimed)",
      topSpeed: "250+ mph",
      packKWh: "~200 kWh tri-motor",
      price: "From $200,000 (Founders $250,000)",
      delivery: "First customer deliveries: 2026",
      chemistry: "Next-gen 4680 / structural pack",
    },
  },
  {
    id: "mach-e-er",
    brand: "ford",
    name: "Mustang Mach-E Ext. Range",
    short: "Mach-E ER",
    year: 2023,
    color: "Grabber Blue",
    vin: "3FMTK3SU5PM••••21",
    odometer: 32180,
    epaRange: 290,
    currentRange: 268,
    sohCurrent: 92.8,
    fleetAvg: 93.4,
    delta30d: -0.2,
    warranty: { years: 8, miles: 100000, threshold: 70 },
  },
  {
    id: "f150-lightning",
    brand: "ford",
    name: "F-150 Lightning Lariat",
    short: "Lightning",
    year: 2023,
    color: "Antimatter Blue",
    vin: "1FTVW1EL5PW••••64",
    odometer: 28540,
    epaRange: 320,
    currentRange: 296,
    sohCurrent: 93.5,
    fleetAvg: 93.9,
    delta30d: -0.2,
    warranty: { years: 8, miles: 100000, threshold: 70 },
  },
  {
    id: "e-transit",
    brand: "ford",
    name: "E-Transit Cargo",
    short: "E-Transit",
    year: 2023,
    color: "Oxford White",
    vin: "1FTBW9CK5PK••••38",
    odometer: 44920,
    epaRange: 126,
    currentRange: 112,
    sohCurrent: 91.2,
    fleetAvg: 92.0,
    delta30d: -0.3,
    warranty: { years: 8, miles: 100000, threshold: 70 },
  },
  {
    id: "volvo-ex40",
    brand: "volvo",
    name: "Volvo EX40 Twin Motor",
    short: "EX40",
    year: 2024,
    color: "Crystal White Pearl",
    vin: "YV4EK3ZL4R••••37",
    odometer: 18420,
    epaRange: 254,
    currentRange: 236,
    sohCurrent: 94.1,
    fleetAvg: 94.0,
    delta30d: -0.1,
    warranty: { years: 8, miles: 100000, threshold: 70 },
  },
  {
    id: "volvo-ex90",
    brand: "volvo",
    name: "Volvo EX90 Twin Motor",
    short: "EX90",
    year: 2025,
    color: "Vapour Grey",
    vin: "YV4H60CF9S••••82",
    odometer: 9640,
    epaRange: 310,
    currentRange: 298,
    sohCurrent: 96.4,
    fleetAvg: 96.1,
    delta30d: -0.1,
    warranty: { years: 8, miles: 100000, threshold: 70 },
  },
  {
    id: "polestar-2",
    brand: "volvo",
    name: "Polestar 2 Long Range",
    short: "Polestar 2",
    year: 2023,
    color: "Snow",
    vin: "LPSED3KA4P••••19",
    odometer: 35880,
    epaRange: 276,
    currentRange: 252,
    sohCurrent: 92.3,
    fleetAvg: 92.8,
    delta30d: -0.2,
    warranty: { years: 8, miles: 100000, threshold: 70 },
  },
];

// Generates an 18-month SoH curve ending at the vehicle's current value,
// with a parallel fleet-average line ending at fleetAvg. Uses an eased decay
// so degradation looks fast early and tapers off — matches typical lithium-ion behavior.
export function buildDegradationSeries(vehicle, monthsBack = 18) {
  const totalLoss = 100 - vehicle.sohCurrent;
  const fleetLoss = 100 - vehicle.fleetAvg;
  const now = new Date();
  const series = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const t = (monthsBack - 1 - i) / (monthsBack - 1); // 0..1 from oldest to newest
    const eased = Math.pow(t, 0.55);
    const soh = +(100 - totalLoss * eased).toFixed(1);
    const fleet = +(100 - fleetLoss * eased).toFixed(1);
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleDateString("en-US", { month: "short", year: "2-digit" }).replace(" ", " '").replace(/'(\d)/, "'$1");
    series.push({ month: label, soh, fleet });
  }
  return series;
}


// ---------------------------------------------------------------------------
// Dynamic (model × year) catalog — regenerated every render based on the
// current calendar year. Each Tesla model started production in a specific
// year; we synthesize realistic SoH/odometer/EPA figures per year.
// ---------------------------------------------------------------------------

const MODEL_LINEUP = [
  {
    modelKey: "model-s",
    model: "Model S",
    short: "Model S",
    startYear: 2012,
    trims: "Long Range",
    baseEpa: 405,
    warranty: { years: 8, miles: 150000, threshold: 70 },
    colorRotation: ["Pearl White Multi-Coat", "Midnight Silver", "Solid Black", "Deep Blue", "Ultra Red"],
  },
  {
    // Model S Plaid — tri-motor, launched June 2021.
    // EPA range varies by wheel size (348 mi on 21" / 396 mi on 19"); we use
    // 396 as baseEpa to match Tesla's headline figure. Same 8yr/150k warranty
    // as the rest of the Model S lineup.
    modelKey: "model-s-plaid",
    model: "Model S Plaid",
    short: "Model S Plaid",
    startYear: 2021,
    trims: "",
    baseEpa: 396,
    warranty: { years: 8, miles: 150000, threshold: 70 },
    colorRotation: ["Pearl White Multi-Coat", "Solid Black", "Midnight Silver", "Deep Blue", "Ultra Red"],
  },
  {
    modelKey: "model-3",
    model: "Model 3",
    short: "Model 3",
    startYear: 2017,
    trims: "Long Range",
    baseEpa: 358,
    warranty: { years: 8, miles: 120000, threshold: 70 },
    colorRotation: ["Pearl White", "Solid Black", "Deep Blue", "Stealth Grey", "Ultra Red"],
  },
  {
    modelKey: "model-x",
    model: "Model X",
    short: "Model X",
    startYear: 2015,
    trims: "Long Range",
    baseEpa: 348,
    warranty: { years: 8, miles: 150000, threshold: 70 },
    colorRotation: ["Solid Black", "Pearl White", "Midnight Cherry", "Ultra Red", "Deep Blue"],
  },
  {
    modelKey: "model-y",
    model: "Model Y",
    short: "Model Y",
    startYear: 2020,
    trims: "Long Range",
    baseEpa: 330,
    warranty: { years: 8, miles: 120000, threshold: 70 },
    colorRotation: ["Deep Blue Metallic", "Pearl White", "Solid Black", "Stealth Grey", "Quicksilver"],
  },
  {
    modelKey: "cybertruck",
    model: "Cybertruck",
    short: "Cybertruck",
    startYear: 2024,
    trims: "Dual Motor",
    baseEpa: 340,
    warranty: { years: 8, miles: 150000, threshold: 70 },
    colorRotation: ["Stainless", "Foundation Black"],
  },
];

// Best-guess EPA-range per (model, year). Later refreshes tend to nudge range up.
function epaForYear(base, ageYears) {
  // Very old cars (pre-2018) — smaller pack, lower range
  const yr = new Date().getFullYear() - ageYears;
  if (yr <= 2013) return Math.round(base * 0.62);
  if (yr <= 2015) return Math.round(base * 0.72);
  if (yr <= 2017) return Math.round(base * 0.85);
  if (yr <= 2019) return Math.round(base * 0.93);
  return base;
}

// Realistic-ish odometer at ~13k mi/year (US avg for EVs).
function odoForAge(ageYears) {
  if (ageYears <= 0) return 1200;
  return Math.round(ageYears * 12800 + (ageYears % 3) * 900);
}

// Realistic-ish SoH: calendar aging (~1-2%/yr young, tapering, asymptote ~12%)
// + cycle aging. Tuned so a 9-year-old Tesla lands around 82-85% (matches
// Tesloop / Recurrent Auto real-world Tesla data), and a 1-year-old car
// stays above 97%.
function sohForAge(ageYears) {
  if (ageYears <= 0) return 100;
  const cal = 12 * (1 - Math.exp(-ageYears / 5)); // asymptotes ~12%
  const cyc = Math.min(14, ageYears * 0.85);       // cycle wear
  return +Math.max(62, 100 - cal - cyc).toFixed(1);
}

// Ballpark fleet-average SoH for the same age band (slightly kinder than one owner).
function fleetForAge(ageYears) {
  return +Math.min(100, sohForAge(ageYears) + 0.9).toFixed(1);
}

function delta30ForAge(ageYears) {
  // Newer cars barely lose anything month-over-month
  if (ageYears <= 1) return -0.1;
  if (ageYears <= 3) return -0.2;
  if (ageYears <= 5) return -0.3;
  return -0.4;
}

/**
 * Build the full model × year catalog through `currentYear` (default: today's year).
 * Each entry has the same shape as the hand-crafted VEHICLES list so all
 * downstream helpers (computeEviqScore, computeRangeScenario, warranty math,
 * degradation series) work unchanged.
 */
export function buildVehicleCatalog(currentYear = new Date().getFullYear()) {
  const out = [];
  MODEL_LINEUP.forEach((m) => {
    // Newest first per model
    for (let y = currentYear; y >= m.startYear; y--) {
      const ageYears = Math.max(0, currentYear - y);
      const color = m.colorRotation[y % m.colorRotation.length];
      const soh = sohForAge(ageYears);
      out.push({
        id: `${m.modelKey}-${y}`,
        modelKey: m.modelKey,
        name: `${m.model} ${m.trims}`.trim(),
        short: `${m.short}${ageYears === 0 ? "" : ""}`,
        year: y,
        color,
        vin: `${m.modelKey.toUpperCase().slice(0, 5)}-${y}-••••`,
        odometer: odoForAge(ageYears),
        epaRange: epaForYear(m.baseEpa, ageYears),
        currentRange: Math.round(epaForYear(m.baseEpa, ageYears) * (soh / 100)),
        sohCurrent: soh,
        fleetAvg: fleetForAge(ageYears),
        delta30d: delta30ForAge(ageYears),
        warranty: m.warranty,
      });
    }
  });

  // Preserve the Roadster preview (announced but unreleased)
  out.push({
    id: "roadster-2026",
    modelKey: "roadster",
    name: "Roadster (next-gen)",
    short: "Roadster 2026",
    year: 2026,
    color: "Velocity Red (announced)",
    vin: "PREVIEW · unreleased",
    odometer: 0,
    epaRange: 620,
    currentRange: 620,
    sohCurrent: 100,
    fleetAvg: 100,
    delta30d: 0,
    warranty: { years: 8, miles: 150000, threshold: 70, estimated: true },
    isPreview: true,
  });

  return out;
}

// Distinct model list — used by pickers that let the user drill in by model, then year.
export const MODEL_LIST = MODEL_LINEUP.map((m) => ({
  modelKey: m.modelKey,
  model: m.model,
  short: m.short,
  startYear: m.startYear,
}));
